import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

// AUPREA — Menu mobile public (hamburger + drawer) réutilisé par les headers Landing / CommentCaMarche / Services
// Visible uniquement < 768px (md:hidden). Ne touche pas au desktop.
// Les liens sont configurables mais ont un jeu par défaut aligné sur la nav Landing.

const DEFAULT_LINKS = [
  { to: "/bilan-succession", label: "Faites votre bilan succession", accent: true, testid: "mob-nav-bilan" },
  { to: "/services", label: "Services sur mesure", testid: "mob-nav-services" },
  { to: "/comment-ca-marche", label: "Comment ça marche", testid: "mob-nav-cmm" },
  { to: "/tarifs", label: "Tarifs", testid: "mob-nav-tarifs" },
  { to: "/connexion", label: "Se connecter", isButton: true, testid: "mob-nav-login" },
];

export default function PublicMobileMenu({ links = DEFAULT_LINKS }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  // Ferme automatiquement à chaque navigation
  useEffect(() => { setOpen(false); }, [location.pathname]);

  // Escape pour fermer + verrouille le scroll body
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = originalOverflow;
    };
  }, [open]);

  return (
    <>
      {/* Bouton hamburger — visible uniquement mobile (< 768px). Zone tactile 44x44 min. */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Ouvrir le menu"
        aria-expanded={open}
        aria-controls="public-mobile-menu-drawer"
        data-testid="mobile-menu-toggle"
        className="md:hidden inline-flex items-center justify-center"
        style={{
          background: "transparent",
          color: "var(--auprea-navy)",
          padding: 10,
          minWidth: 44,
          minHeight: 44,
          border: "1px solid rgba(21,41,79,0.15)",
          borderRadius: 4,
        }}
      >
        <Menu size={22} />
      </button>

      {/* Drawer plein-écran mobile */}
      {open && (
        <div
          id="public-mobile-menu-drawer"
          role="dialog"
          aria-modal="true"
          aria-label="Menu de navigation"
          data-testid="mobile-menu-drawer"
          className="md:hidden flex justify-end"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9995,
            background: "rgba(15,20,35,0.55)",
            backdropFilter: "blur(4px)",
          }}
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
        >
          <nav
            aria-label="Navigation principale mobile"
            style={{
              background: "var(--auprea-cream)",
              width: "min(88vw, 340px)",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              boxShadow: "-12px 0 40px rgba(0,0,0,0.35)",
              overflowY: "auto",
            }}
          >
            {/* Header du drawer */}
            <div style={{ padding: "18px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid rgba(21,41,79,0.1)" }}>
              <span
                style={{
                  fontFamily: "Spectral, serif",
                  fontSize: 18,
                  color: "var(--auprea-navy)",
                  letterSpacing: "0.15em",
                  textTransform: "uppercase",
                }}
              >
                Menu
              </span>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Fermer le menu"
                data-testid="mobile-menu-close"
                style={{
                  background: "transparent",
                  color: "var(--auprea-navy)",
                  padding: 8,
                  minWidth: 40,
                  minHeight: 40,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                autoFocus
              >
                <X size={22} />
              </button>
            </div>

            {/* Liens */}
            <div style={{ padding: "12px 20px", display: "flex", flexDirection: "column", flex: 1 }}>
              {links.filter((l) => !l.isButton).map((l) => (
                <Link
                  key={l.to + l.label}
                  to={l.to}
                  data-testid={l.testid}
                  onClick={() => setOpen(false)}
                  style={{
                    display: "block",
                    padding: "16px 4px",
                    fontSize: 15,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    fontWeight: l.accent ? 600 : 500,
                    color: l.accent ? "var(--auprea-gold)" : "var(--auprea-navy)",
                    borderBottom: "1px solid rgba(21,41,79,0.08)",
                    minHeight: 48,
                    lineHeight: 1.35,
                  }}
                >
                  {l.label}
                </Link>
              ))}

              {/* Boutons (ex. Se connecter) — collés en bas du drawer */}
              <div style={{ marginTop: "auto", paddingTop: 20, display: "flex", flexDirection: "column", gap: 10 }}>
                {links.filter((l) => l.isButton).map((l) => (
                  <Link
                    key={l.to + l.label}
                    to={l.to}
                    data-testid={l.testid}
                    onClick={() => setOpen(false)}
                    style={{ display: "block", width: "100%" }}
                  >
                    <button
                      type="button"
                      className="auprea-btn"
                      style={{
                        width: "100%",
                        padding: "14px 20px",
                        fontSize: 14,
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                        fontWeight: 600,
                        minHeight: 48,
                      }}
                    >
                      {l.label}
                    </button>
                  </Link>
                ))}
              </div>
            </div>
          </nav>
        </div>
      )}
    </>
  );
}
